import { create } from 'zustand';
import { api } from '@/api/client';

export type NotificationCategory = 'review' | 'follow' | 'stock' | 'change' | 'shipped';
export type NotificationPreferences = {
  sound: boolean;
  speech: boolean;
  categories: Record<NotificationCategory, boolean>;
};
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  sound: false,
  speech: false,
  categories: { review: true, follow: true, stock: true, change: true, shipped: true },
};
const localKey = (userId: string) => `ims.v2.notification-preferences.${userId}`;
let saveQueue: Promise<unknown> = Promise.resolve();

function valid(value: unknown): value is NotificationPreferences {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<NotificationPreferences>;
  return typeof v.sound === 'boolean' && typeof v.speech === 'boolean'
    && !!v.categories && (['review', 'follow', 'stock', 'change', 'shipped'] as NotificationCategory[]).every((key) => typeof v.categories?.[key] === 'boolean');
}

type State = {
  owner: string | null;
  value: NotificationPreferences;
  loaded: boolean;
  load: (userId: string) => Promise<void>;
  save: (userId: string, value: NotificationPreferences) => Promise<'synced' | 'local'>;
};

export const useNotificationPreferences = create<State>((set, get) => ({
  owner: null,
  value: DEFAULT_NOTIFICATION_PREFERENCES,
  loaded: false,
  async load(userId) {
    set({ owner: userId, value: DEFAULT_NOTIFICATION_PREFERENCES, loaded: false });
    let local: NotificationPreferences | null = null;
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(localKey(userId)) || 'null');
      if (valid(parsed)) local = parsed;
    } catch { /* storage unavailable */ }
    if (local) set({ value: local });
    if (import.meta.env.VITE_READONLY !== '0' && local) { set({ loaded: true }); return; }
    try {
      const remote = await api.get<NotificationPreferences | null>('/api/v2/me/notification-preferences');
      if (get().owner === userId) set({ value: valid(remote) ? remote : local || DEFAULT_NOTIFICATION_PREFERENCES, loaded: true });
    } catch { if (get().owner === userId) set({ loaded: true }); }
  },
  async save(userId, value) {
    if (!valid(value)) throw new Error('通知设置不正确');
    if (get().owner === userId) set({ value });
    try { localStorage.setItem(localKey(userId), JSON.stringify(value)); } catch { /* local storage unavailable */ }
    if (import.meta.env.VITE_READONLY !== '0') return 'local';
    try {
      const pending = saveQueue.catch(() => undefined).then(() => api.patch<NotificationPreferences>('/api/v2/me/notification-preferences', value));
      saveQueue = pending;
      await pending;
      return 'synced';
    } catch { return 'local'; }
  },
}));
